import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { Button } from 'antd';
import { FaFilter } from 'react-icons/fa';
import { filters } from '@/constants/filterConfigs';
import { SOSMultiSelectCheckboxDropDown } from '@/components/inputs/multi-select-checkbox-dropdown';
import { SOSCheckBoxes } from '@/components/inputs/check-box';
import { useFetchQuery } from '@/hooks/useFetchQuery';
import { fetchFiltersApi } from '@/services/commonServices';
import { addAlerts } from '@/pages/alerts/alertSlice';
import {
  addFilters,
  clearFilters,
  updateAlertType,
  updatePageNumber,
} from '@/layout/common-filter/filtersSlice';
import { FiltersContainer } from '@/layout/common-filter/styles/Filters.styles';
import { useBaseFilter, convertFinalKey } from './BaseFilter';

/**
 * Alert Filter Component - Filters for alert page
 * @return {JSX.Element} Alert filter component
 */
export const AlertFilter = () => {
  const page = 'ALERT_PAGE';
  const filterData = filters[page];

  // Fetch filter options from API
  const { data } = useFetchQuery({
    currNav: '/alerts/filters',
    qryFn: () => fetchFiltersApi(),
    key: 'filter',
  });

  // Use base filter hook
  const {
    dispatch,
    filtersObj,
    reff,
    setReff,
    defaultVal,
    mergedData,
    activeFilterCount,
  } = useBaseFilter({ page, filterData, apiData: data });

  // Dispatch alert type on load (only if not already set from persisted state)
  React.useEffect(() => {
    if (data?.ALERT_TYPE && data.ALERT_TYPE.length > 0) {
      const alertTypeArray = data.ALERT_TYPE.filter(
        (alert) => alert.isSelected,
      ).map((alert) => alert.value);

      // Only update if alertType is empty (not persisted or cleared)
      if (!filtersObj.alertType || filtersObj.alertType.length === 0) {
        dispatch(updateAlertType(alertTypeArray));
      }
    }
  }, [data, dispatch, filtersObj.alertType]);

  /**
   * Handle filter change
   */
  const handleChange = useCallback(
    (e, filter) => {
      const key = convertFinalKey(filter.name);
      const value = key === 'alertType' && e.length === 0 ? [74, 75, 73] : e;

      dispatch(addAlerts([]));
      dispatch(updatePageNumber(0));
      dispatch(addFilters({ key, value }));
    },
    [dispatch],
  );

  /**
   * Clear all filters
   */
  const clearFilterData = useCallback(() => {
    const isExists = Object.hasOwn(filtersObj, 'alertName');
    const clearPayload = {
      alertType: filtersObj.alertType,
      page: 0,
      application: [],
      priority: [],
      resourceGroup: [],
      businessFunc: [],
      namespace: [],
    };

    if (isExists) {
      clearPayload.alertName = filtersObj.alertName;
    }

    dispatch(clearFilters(clearPayload));
    setReff(false);
    setTimeout(() => setReff(true), 1);
  }, [dispatch, filtersObj, setReff]);

  /**
   * Render filter element
   */
  const renderElement = useCallback(
    (filter) => {
      const filterKey = convertFinalKey(filter.name);
      const currentValue = filtersObj[filterKey];

      switch (filter.type) {
        case 'DROP_DOWN_WITH_FILTER':
          return (
            <div className="filter-item" key={filter.name}>
              <span className="title">{filter.actualName || filter.name}</span>
              <SOSMultiSelectCheckboxDropDown
                handleChange={(values) => handleChange(values, filter)}
                placeholder={filter.actualName}
                options={filter.options}
                defaultValue={currentValue || []}
                key={filter.key}
              />
            </div>
          );

        case 'CHECKBOXES': {
          const isAlertType = filter.name === 'ALERT_TYPE';
          const checkboxKey = isAlertType
            ? `${filter.key}-${defaultVal.join(',')}`
            : filter.key;

          return (
            <div className="filter-item" key={filter.name}>
              <span className="title">{filter.actualName || filter.name}</span>
              <SOSCheckBoxes
                handleChange={(e) => handleChange(e, filter)}
                options={filter.options}
                defaultValue={isAlertType ? defaultVal : []}
                key={checkboxKey}
              />
            </div>
          );
        }

        default:
          return null;
      }
    },
    [handleChange, defaultVal, filtersObj],
  );

  return (
    <FiltersContainer>
      {reff && (
        <>
          <div className="filter-header">
            <h3>
              <span className="header-text">
                <FaFilter className="filter-icon" />
                Alert Filters
              </span>
              {activeFilterCount > 0 && (
                <span className="filter-count">{activeFilterCount}</span>
              )}
            </h3>
          </div>

          <div className="filters-content">
            {mergedData.map((filter) => renderElement(filter))}
          </div>

          <div className="filter-actions">
            <Button
              type="primary"
              danger
              size="large"
              onClick={clearFilterData}
            >
              Clear All Filters
            </Button>
          </div>
        </>
      )}
    </FiltersContainer>
  );
};

AlertFilter.propTypes = {
  hideFilter: PropTypes.func,
};
